import React, { useContext, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import TreeView from "@material-ui/lab/TreeView";
import StyledTreePartnerItem from "./StyledTreePartnerItem";
import useTreeItemStyles from "./partnerstyledtreeitem.styles";
import { Box } from "@mui/material";
import { useLocation } from "react-router-dom";
import { hasPrivilege } from "../utils/privilege";
import { userDetailContext } from "../App";

const createEmojiIcon = (symbol) => {
  function EmojiIcon(props) {
    return <span className={props.className}>{symbol}</span>;
  }
  return EmojiIcon;
};

const DashboardIcon = createEmojiIcon("⊞");
const PartenairesIcon = createEmojiIcon("🏢");
const MarchesIcon = createEmojiIcon("📋");
const EvaluationsIcon = createEmojiIcon("📊");
const ResiliesIcon = createEmojiIcon("🚫");
const CompletedContractsIcon = createEmojiIcon("✓");
const PersonnelIcon = createEmojiIcon("👥");
const MultiIcon = createEmojiIcon("⚠");
const ConfigIcon = createEmojiIcon("🗂");
const UpdateIcon = createEmojiIcon("🔄");
const GuideIcon = createEmojiIcon("📖");

const linkRoot = "/partner";

const NAV_SECTIONS = [
  {
    title: "Vue globale",
    items: [
      { key: "dashboard", icon: DashboardIcon, label: "Tableau de bord", link: `${linkRoot}/dashboard` },
    ],
  },
  {
    title: "Partenaires",
    items: [
      { key: "partenaires", icon: PartenairesIcon, label: "Partenaires", badgeKey: "partenaires", badgeTone: "b", link: `${linkRoot}` },
      { key: "marches", icon: MarchesIcon, label: "Marchés/ Contrats", badgeKey: "marches", badgeTone: "b", link: `${linkRoot}/procurement` },
      { key: "evaluations", icon: EvaluationsIcon, label: "Évaluations", badgeKey: "evaluations", badgeTone: "y", link: `${linkRoot}/evaluations` },
    ],
  },
  {
    title: "Suivi",
    items: [
      { key: "resilies", icon: ResiliesIcon, label: "Contrats résiliés", badgeKey: "resilies", badgeTone: "r", link: `${linkRoot}/terminated` },
      { key: "contratsAcheves", icon: CompletedContractsIcon, label: "Contrats achevés", badgeKey: "contratsAcheves", badgeTone: "g", link: `${linkRoot}/completed-contracts` },
      { key: "personnel", icon: PersonnelIcon, label: "Personnel (contrôle)", badgeKey: "personnel", link: `${linkRoot}/staff` },
      { key: "multi", icon: MultiIcon, label: "Multi-partenaires", badgeKey: "multi", badgeTone: "y", link: `${linkRoot}/multi` },
    ],
  },
  {
    title: "Administration",
    items: [
      { key: "basesImportees", icon: ConfigIcon, label: "Bases importées", link: `${linkRoot}/database` },
      { key: "validation-resilies", icon: UpdateIcon, label: "Validation contrats résiliés", badgeKey: "validation-resilies", link: `${linkRoot}/validation-resilies` },
    ],
  },
  {
    title: "Mises à jour",
    items: [
      { key: "misesAJour", icon: UpdateIcon, label: "Importer des données", link: `${linkRoot}/updates` },
    ],
  },
  {
    title: "Aide",
    footer: true,
    items: [
      { key: "guide", icon: GuideIcon, label: "Guide utilisateur", link: `${linkRoot}/guide` },
    ],
  },
];

const normalizePath = (path) => {
  const cleanPath = String(path || "").split("?")[0].split("#")[0].trim();
  if (!cleanPath) return "/";
  if (cleanPath.length > 1) return cleanPath.replace(/\/+$/, "");
  return cleanPath;
};

const isPathMatch = (pathname, itemPath) =>
  pathname === itemPath || pathname.startsWith(`${itemPath}/`);

const PartnerSidebarTreeView = ({ activeKey = "dashboard", badgeCounts = {}, onSelect, onNavigate }) => {
  const classes = useTreeItemStyles();
  const location = useLocation();
  const contextData = useContext(userDetailContext);
  const [selectedKey, setSelectedKey] = useState(activeKey);
  const navSections = useMemo(() => {
    const canManagePartnerAdmin = hasPrivilege(contextData.privileges, "admin_partenaire");
    const canValidateResiliation = hasPrivilege(
      contextData.privileges,
      "valider_resiliation_partenaire"
    );
    const canImportPartnerData = canManagePartnerAdmin
      || hasPrivilege(contextData.privileges, "importer_donnees_partner");

    return NAV_SECTIONS
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => {
          if (item.key === "basesImportees") return canManagePartnerAdmin;
          if (item.key === "validation-resilies") return canValidateResiliation;
          if (item.key === "misesAJour") return canImportPartnerData;
          return true;
        }),
      }))
      .filter((section) => section.items.length > 0);
  }, [contextData.privileges]);

  const flatItems = useMemo(() => navSections.flatMap((section) => section.items), [navSections]);

  const activeKeyFromPath = useMemo(() => {
    const pathname = normalizePath(location.pathname);
    const matchedItem = [...flatItems]
      .filter((item) => isPathMatch(pathname, normalizePath(item.link)))
      .sort((a, b) => normalizePath(b.link).length - normalizePath(a.link).length)[0];

    return matchedItem?.key || null;
  }, [flatItems, location.pathname]);

  useEffect(() => {
    if (activeKeyFromPath) {
      setSelectedKey(activeKeyFromPath);
      return;
    }
    setSelectedKey(activeKey);
  }, [activeKey, activeKeyFromPath]);

  const handleSelect = (_, nodeId) => {
    setSelectedKey(nodeId);
    if (typeof onSelect === "function") {
      onSelect(nodeId);
    }
    if (typeof onNavigate === "function") {
      onNavigate();
    }
  };



  return (
    <nav className={classes.sidebarRoot} aria-label="Navigation partenaire">
      {navSections.map((section) => (
        <Box className={clsx(classes.section, section.footer && classes.footerSection)} key={section.title}>
          <div className={classes.sectionTitle}>{section.title}</div>
          <TreeView
            className={classes.treeView}
            defaultCollapseIcon={<span />}
            defaultExpandIcon={<span />}
            defaultEndIcon={<span />}
            selected={selectedKey}
            onNodeSelect={handleSelect}
          >
            {section.items.map((item) => (
              <StyledTreePartnerItem
                key={item.key}
                id={item.id}
                nodeId={item.key}
                label={item.label}
                labelText={item.label}
                labelIcon={item.icon}
                isBold
                link={item.link}
                isActiveMenu={selectedKey === item.key}
                labelInfoClassName={clsx({
                  [classes.badgeBlue]: item.badgeTone === "b",
                  [classes.badgeRed]: item.badgeTone === "r",
                  [classes.badgeYellow]: item.badgeTone === "y",
                  [classes.badgeGreen]: item.badgeTone === "g",
                })}
              />
            ))}
          </TreeView>
        </Box>
      ))}
    </nav>
  );
};

export default PartnerSidebarTreeView;
